/**
 * Menu Permissions Mapping
 * Maps each menu path to its required permission resource
 * This allows granular control over which menus users can access based on their permissions
 */

export interface MenuPermissionMapping {
  path: string;
  resource: string; // The permission resource name (e.g., 'menu.dashboard')
  isPublic?: boolean; // If true, accessible to all authenticated users
}

// Map menu paths to their permission resources
export const menuPermissionMappings: MenuPermissionMapping[] = [
  // Dashboard (public to all users)
  { path: '/dashboard', resource: 'menu.dashboard', isPublic: true },
  { path: '/dashboard/notifications', resource: 'menu.notifications', isPublic: true },
  
  // My Account (public to all users)
  { path: '/dashboard/wallet', resource: 'menu.wallet', isPublic: true },
  { path: '/dashboard/rewards', resource: 'menu.rewards', isPublic: true },
  { path: '/dashboard/invoices', resource: 'menu.my_invoices', isPublic: true },
  { path: '/dashboard/payment-methods', resource: 'menu.payment_methods', isPublic: true },
  { path: '/dashboard/settings', resource: 'menu.settings', isPublic: true },
  
  // Partner Program (public to all users)
  { path: '/dashboard/join', resource: 'menu.partner', isPublic: true },
  { path: '/dashboard/affiliate', resource: 'menu.affiliate', isPublic: true },
  { path: '/dashboard/partner-portal', resource: 'menu.mlm_portal', isPublic: true },
  { path: '/marketer', resource: 'menu.marketer', isPublic: true },
  
  // Content
  { path: '/dashboard/qr-codes', resource: 'menu.qr_codes' },
  { path: '/dashboard/qr-codes/create', resource: 'menu.qr_codes' },
  { path: '/dashboard/my-pages', resource: 'menu.my_pages' },
  { path: '/dashboard/menu-builder', resource: 'menu.menu_builder' },
  { path: '/dashboard/form-builder', resource: 'menu.form_builder' },
  { path: '/dashboard/forms', resource: 'menu.forms', isPublic: true },
  { path: '/dashboard/files', resource: 'menu.files' },
  
  // Analytics (public to all users)
  { path: '/dashboard/analytics', resource: 'menu.analytics', isPublic: true },
  { path: '/dashboard/audit-history', resource: 'menu.activity_log', isPublic: true },
  { path: '/dashboard/analysis-history', resource: 'menu.analysis_history', isPublic: true },
  
  // Customers (Admin only)
  { path: '/dashboard/clients', resource: 'menu.clients' },
  { path: '/dashboard/contacts', resource: 'menu.contacts' },
  
  // Sales
  { path: '/dashboard/estimates', resource: 'menu.estimates' },
  { path: '/dashboard/proposals', resource: 'menu.proposals' },
  { path: '/dashboard/proposal-generator', resource: 'menu.proposal_generator' },
  { path: '/dashboard/admin-invoices', resource: 'menu.admin_invoices' },
  { path: '/dashboard/admin-payments', resource: 'menu.admin_payments' },
  { path: '/dashboard/credit-notes', resource: 'menu.credit_notes' },
  { path: '/dashboard/coupons', resource: 'menu.coupons' },
  
  // Subscriptions
  { path: '/dashboard/subscriptions', resource: 'menu.subscriptions' },
  
  // Expenses
  { path: '/dashboard/expenses', resource: 'menu.expenses' },
  
  // Contracts
  { path: '/dashboard/admin-contracts', resource: 'menu.contracts' },
  
  // Projects
  { path: '/dashboard/crm-projects', resource: 'menu.projects' },
  { path: '/dashboard/tasks', resource: 'menu.tasks' },
  { path: '/dashboard/tasks-kanban', resource: 'menu.kanban' },
  { path: '/dashboard/time-tracking', resource: 'menu.time_tracking' },
  { path: '/dashboard/milestones', resource: 'menu.milestones' },
  
  // Support
  { path: '/dashboard/support-tickets', resource: 'menu.tickets' },
  { path: '/dashboard/knowledge-base', resource: 'menu.knowledge_base' },
  
  // Leads
  { path: '/dashboard/leads', resource: 'menu.leads' },
  { path: '/dashboard/sales-funnel', resource: 'menu.sales_funnel' },
  
  // HRM
  { path: '/dashboard/hr', resource: 'menu.hr' },
  { path: '/dashboard/staff', resource: 'menu.staff' },
  { path: '/dashboard/departments', resource: 'menu.departments' },
  { path: '/dashboard/leave-management', resource: 'menu.leave_management' },
  { path: '/dashboard/performance-reviews', resource: 'menu.performance' },
  { path: '/dashboard/attendance', resource: 'menu.attendance' },
  
  // E-commerce
  { path: '/dashboard/ecommerce', resource: 'menu.ecommerce' },
  { path: '/admin-products', resource: 'menu.products' },
  { path: '/admin-orders', resource: 'menu.orders' },
  { path: '/admin-categories', resource: 'menu.categories' },
  
  // Booking
  { path: '/dashboard/booking', resource: 'menu.booking' },
  { path: '/dashboard/admin-appointments', resource: 'menu.appointments' },
  { path: '/dashboard/admin-reservations', resource: 'menu.reservations' },
  { path: '/admin/restaurants', resource: 'menu.restaurants' },
  { path: '/dashboard/admin-floorplan', resource: 'menu.floorplan' },
  { path: '/dashboard/admin-services', resource: 'menu.services' },
  
  // Gift Cards
  { path: '/dashboard/gift-cards', resource: 'menu.gift_cards' },
  
  // Marketing
  { path: '/dashboard/marketing', resource: 'menu.campaigns' },
  { path: '/dashboard/email-templates', resource: 'menu.email_templates' },
  { path: '/dashboard/mlm-programs', resource: 'menu.mlm_programs' },
  { path: '/dashboard/session-replays', resource: 'menu.session_replays' },
  
  // Content Management
  { path: '/dashboard/blog', resource: 'menu.blog' },
  { path: '/dashboard/blog/content-strategy', resource: 'menu.blog' },
  { path: '/dashboard/blog-categories', resource: 'menu.blog_categories' },
  { path: '/dashboard/wordpress-import', resource: 'menu.wordpress_import' },
  
  // Reports
  { path: '/dashboard/reports', resource: 'menu.reports' },
  
  // AI Phone Agent
  { path: '/dashboard/ai-phone-command-center', resource: 'menu.ai_phone_command' },
  { path: '/dashboard/ai-phone-agent', resource: 'menu.ai_phone_agent' },
  
  // Utilities
  { path: '/dashboard/events', resource: 'menu.calendar' },
  { path: '/dashboard/announcements', resource: 'menu.announcements' },
  { path: '/dashboard/notes', resource: 'menu.notes' },
  { path: '/dashboard/messages', resource: 'menu.messages' },
  { path: '/dashboard/reminders', resource: 'menu.reminders' },
  
  // SaaS
  { path: '/dashboard/saas-features', resource: 'menu.saas_features' },
  { path: '/dashboard/subscription-plans', resource: 'menu.subscription_plans' },
  { path: '/dashboard/ai-editors', resource: 'menu.ai_editors' },
  { path: '/dashboard/user-activity', resource: 'menu.user_activity' },
  
  // Setup
  { path: '/dashboard/user-management', resource: 'menu.user_management' },
  { path: '/dashboard/roles', resource: 'menu.roles' },
  { path: '/dashboard/security', resource: 'menu.security' },
  { path: '/dashboard/site-config', resource: 'menu.site_config' },
  { path: '/dashboard/page-seo', resource: 'menu.page_seo' },
  { path: '/dashboard/tenants', resource: 'menu.tenants' },
  { path: '/dashboard/api-gateway', resource: 'menu.api_gateway' },
  { path: '/dashboard/api-settings', resource: 'menu.api_settings' },
  { path: '/dashboard/admin', resource: 'menu.admin_panel' },
];

// Helper to get permission resource for a path
export const getMenuPermission = (path: string): MenuPermissionMapping | undefined => {
  return menuPermissionMappings.find(m => m.path === path);
};

// Helper to check if a path is public
export const isPublicMenu = (path: string): boolean => {
  const mapping = getMenuPermission(path);
  return mapping?.isPublic ?? false;
};

// Category to permission resources mapping
// Categories with isPublic: true are accessible to all authenticated users
export const categoryPermissions: Record<string, { resources: string[], isPublic?: boolean }> = {
  dashboard: { resources: ['menu.dashboard', 'menu.notifications'], isPublic: true },
  myAccount: { resources: ['menu.wallet', 'menu.rewards', 'menu.my_invoices', 'menu.payment_methods', 'menu.settings'], isPublic: true },
  partner: { resources: ['menu.partner', 'menu.affiliate', 'menu.mlm_portal', 'menu.marketer'], isPublic: true },
  content: { resources: ['menu.qr_codes', 'menu.my_pages', 'menu.menu_builder', 'menu.form_builder', 'menu.forms', 'menu.files'] },
  analytics: { resources: ['menu.analytics', 'menu.activity_log', 'menu.analysis_history'], isPublic: true },
  customers: { resources: ['menu.clients', 'menu.contacts'] },
  sales: { resources: ['menu.estimates', 'menu.proposals', 'menu.proposal_generator', 'menu.admin_invoices', 'menu.admin_payments', 'menu.credit_notes', 'menu.coupons'] },
  subscriptions: { resources: ['menu.subscriptions'] },
  expenses: { resources: ['menu.expenses'] },
  contracts: { resources: ['menu.contracts'] },
  projects: { resources: ['menu.projects', 'menu.tasks', 'menu.kanban', 'menu.time_tracking', 'menu.milestones'] },
  support: { resources: ['menu.tickets', 'menu.knowledge_base'] },
  leads: { resources: ['menu.leads', 'menu.sales_funnel'] },
  hrm: { resources: ['menu.hr', 'menu.staff', 'menu.departments', 'menu.leave_management', 'menu.performance', 'menu.attendance'] },
  ecommerce: { resources: ['menu.ecommerce', 'menu.products', 'menu.orders', 'menu.categories'] },
  booking: { resources: ['menu.booking', 'menu.appointments', 'menu.reservations', 'menu.floorplan', 'menu.services'] },
  giftCards: { resources: ['menu.gift_cards'] },
  marketing: { resources: ['menu.campaigns', 'menu.email_templates', 'menu.mlm_programs', 'menu.session_replays'] },
  contentManagement: { resources: ['menu.blog', 'menu.blog_categories', 'menu.wordpress_import'] },
  reports: { resources: ['menu.reports'] },
  aiPhone: { resources: ['menu.ai_phone_command', 'menu.ai_phone_agent'] },
  utilities: { resources: ['menu.calendar', 'menu.announcements', 'menu.notes', 'menu.messages', 'menu.reminders'] },
  saas: { resources: ['menu.saas_features', 'menu.subscription_plans', 'menu.ai_editors', 'menu.user_activity'] },
  setup: { resources: ['menu.user_management', 'menu.roles', 'menu.security', 'menu.site_config', 'menu.tenants', 'menu.api_gateway', 'menu.api_settings', 'menu.admin_panel'] },
};

// Helper to check if a category is public
export const isPublicCategory = (categoryKey: string): boolean => {
  return categoryPermissions[categoryKey]?.isPublic ?? false;
};

// Get resources for a category
export const getCategoryResources = (categoryKey: string): string[] => {
  return categoryPermissions[categoryKey]?.resources ?? [];
};
